import React from "react";
import { fromJS } from "immutable";
import { Input, Select, SelectOption } from "@showitinc/showit-wc";

import AppConstants from "../constants/AppConstants";
import { elementSyncUtils } from "../utils/SyncUtils";
import SplitMergeWrapper from "../app2components/base/SplitMergeWrapper";
import {
  ANIMATION_DELAY,
  ANIMATION_DIRECTION,
  ANIMATION_DURATION,
  ANIMATION_TYPE,
} from "../constants/MergeConstants";
import When from "./utils/When";
import LeftOffset from "./base/LeftOffset";
import {
  DEFAULT_TRANSITION_IN,
  STATE_TRANSITION_IN,
  TRANSITION_IN,
} from "../constants/AnimationConstants";
import { INPUT_TYPE_NUMBER } from "../constants/ElementConstants";
import { initFormatter } from "../utils/PropertyFormatters";

const { ANIMATION_TYPE_FADE } = AppConstants;

const {
  ANIMATION_TYPE_BLOCK,
  ANIMATION_TYPE_PAGE,
  ANIMATION_TYPE_NONE,
  ANIMATION_TYPE_SLIDE,
  ANIMATION_TYPE_BOUNCE,
  ANIMATION_TYPE_ROTATE,
  ANIMATION_TYPE_ZOOM,
  ANIMATION_TYPE_FLIP,
  ANIMATION_TYPE_ROLL,
  ANIMATION_TYPE_LIGHT_SPEED,
  ANIMATION_DIRECTION_UP,
  ANIMATION_DIRECTION_DOWN,
  ANIMATION_DIRECTION_LEFT,
  ANIMATION_DIRECTION_RIGHT,
  ANIMATION_DIRECTION_DOWN_LEFT,
  ANIMATION_DIRECTION_DOWN_RIGHT,
  ANIMATION_DIRECTION_UP_LEFT,
  ANIMATION_DIRECTION_UP_RIGHT,
  ANIMATION_DIRECTION_X,
  ANIMATION_DIRECTION_Y,
  LAYOUT_DESKTOP,
  UPDATE_ACTION_DELETE,
} = AppConstants;

const ANIMATION_OPTIONS_MAP = [
  [ANIMATION_TYPE_NONE, "None"],
  [ANIMATION_TYPE_FADE, "Fade"],
  [ANIMATION_TYPE_SLIDE, "Slide"],
  [ANIMATION_TYPE_BOUNCE, "Bounce"],
  [ANIMATION_TYPE_ROTATE, "Rotate"],
  [ANIMATION_TYPE_ZOOM, "Zoom"],
  [ANIMATION_TYPE_FLIP, "Flip"],
  [ANIMATION_TYPE_ROLL, "Roll"],
  [ANIMATION_TYPE_LIGHT_SPEED, "Light Speed"],
];

const ANIMATION_DIRECTIONS_MAP = [
  [ANIMATION_DIRECTION_UP, "Up"],
  [ANIMATION_DIRECTION_DOWN, "Down"],
  [ANIMATION_DIRECTION_LEFT, "Left"],
  [ANIMATION_DIRECTION_RIGHT, "Right"],
];
const ANIMATION_ROTATION_DIRECTION_MAP = [
  [ANIMATION_DIRECTION_DOWN_LEFT, "Down Left"],
  [ANIMATION_DIRECTION_DOWN_RIGHT, "Down Right"],
  [ANIMATION_DIRECTION_UP_LEFT, "Up Left"],
  [ANIMATION_DIRECTION_UP_RIGHT, "Up Right"],
];
const ANIMATION_FLIP_DIRECTION_MAP = [
  [ANIMATION_DIRECTION_X, "Horizontal"],
  [ANIMATION_DIRECTION_Y, "Vertical"],
];

const TYPE_DIRECTION_OPTIONS = {
  [ANIMATION_TYPE_BOUNCE]: ANIMATION_DIRECTIONS_MAP,
  [ANIMATION_TYPE_SLIDE]: ANIMATION_DIRECTIONS_MAP,
  [ANIMATION_TYPE_ZOOM]: ANIMATION_DIRECTIONS_MAP,
  [ANIMATION_TYPE_ROTATE]: ANIMATION_ROTATION_DIRECTION_MAP,
  [ANIMATION_TYPE_FLIP]: ANIMATION_FLIP_DIRECTION_MAP,
};

const defaults = fromJS({
  type: ANIMATION_TYPE_NONE,
  duration: 0.5,
  delay: 0,
  direction: ANIMATION_DIRECTION_DOWN,
});

const AnimationProperties = ({
  onPropUpdate,
  container,
  layout,
  transitionType = "",
  defaultType = ANIMATION_TYPE_NONE,
  showPageDefault = true,
  showBlockDefault = true,
  isLocked = false,
}) => {
  const _onPropChange = (propInfo) => {
    onPropUpdate && onPropUpdate(propInfo);
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    container,
    _onPropChange,
    layout,
    "",
    transitionType,
    undefined
  );
  const addTransitionPath = (propName) =>
    transitionType ? `${transitionType}.${propName}` : propName;

  const _onAnimationChange = (propInfo) => {
    const { value, name } = propInfo;

    // Remove entire tranProp if value is set to none, delete the whole transition object (trIn/trOut/defaultTrIn/etc.)
    const transitionPath = name.split(".").slice(0, -1).join(".");
    const propChanges = (value === defaultType && value === ANIMATION_TYPE_NONE)
      ? [{ name: transitionPath, action: UPDATE_ACTION_DELETE }]
      : (
          value === ANIMATION_TYPE_PAGE || value === ANIMATION_TYPE_BLOCK ?
            // If the value is page or block, then delete the transition object and add the type prop back
            [{ name: transitionPath, action: UPDATE_ACTION_DELETE }, { name, value }] :
            [propInfo]
        );

    // Remove direction prop for animations without directions (fade, roll, light speed)
    // Filters for values that are in the ANIMATION_OPTIONS_MAP
    ANIMATION_OPTIONS_MAP.map((option) => option[0]).includes(value) &&
      !Object.keys(TYPE_DIRECTION_OPTIONS).includes(value) &&
      propChanges.push({
        name: getPropName(addTransitionPath(ANIMATION_DIRECTION), addTransitionPath(ANIMATION_TYPE)),
        action: UPDATE_ACTION_DELETE,
      });

    // Default to the first direction in the dropdown for animations with directions (slide, bounce, rotate, zoom, flip)
    Object.keys(TYPE_DIRECTION_OPTIONS).includes(value) &&
      propChanges.push({
        name: getPropName(addTransitionPath(ANIMATION_DIRECTION), addTransitionPath(ANIMATION_TYPE)),
        value: TYPE_DIRECTION_OPTIONS[value][0][0],
      });
    _onPropChange(propChanges);
  };

  const type = container.getIn(
    [
      isSynced(addTransitionPath(ANIMATION_TYPE)) ? LAYOUT_DESKTOP : layout,
      transitionType,
      ANIMATION_TYPE,
    ],
    defaults.get(ANIMATION_TYPE)
  );
  const typeNotNoneOrInherit =
    type !== ANIMATION_TYPE_NONE && type !== ANIMATION_TYPE_PAGE && type !== ANIMATION_TYPE_BLOCK;

  const getSyncValue = (propName) =>
    isSynced(addTransitionPath(ANIMATION_TYPE))
      ? container.getIn([LAYOUT_DESKTOP, transitionType, propName], defaults.get(propName))
      : container.getIn([layout, transitionType, propName], defaults.get(propName));

  const prepChange = (name) => [name, getSyncValue(name)];
  const { onChange } = initFormatter(_onAnimationChange, (name) =>
    getPropName(addTransitionPath(name), addTransitionPath(ANIMATION_TYPE))
  );

  return (
    <SplitMergeWrapper
      layout={layout}
      sync={isSynced(addTransitionPath(ANIMATION_TYPE))}
      toggleSync={() => toggleSyncedProp(ANIMATION_TYPE)}
    >
      <Select
        label="Type"
        reserveContext
        disabled={isLocked}
        onSISelect={onChange(ANIMATION_TYPE, type)}
        value={type}
        context
        shift="left"
      >
        {showBlockDefault && (
          <SelectOption value={ANIMATION_TYPE_BLOCK}>Canvas Default</SelectOption>
        )}
        {showPageDefault && <SelectOption value={ANIMATION_TYPE_PAGE}>Page Default</SelectOption>}
        {ANIMATION_OPTIONS_MAP.map(([value, label]) => (
          <SelectOption key={`${transitionType}${value}`} value={value}>
            {label}
          </SelectOption>
        ))}
      </Select>

      <When it={typeNotNoneOrInherit || TYPE_DIRECTION_OPTIONS[type]}>
        <LeftOffset>
          {typeNotNoneOrInherit && (
            <>
              <Input
                label="Time"
                type={INPUT_TYPE_NUMBER}
                min="0"
                disabled={isLocked}
                step=".25"
                value={getSyncValue(ANIMATION_DURATION)}
                units="sec"
                onSIBeforeChange={onChange(...prepChange(ANIMATION_DURATION))}
                onSIInput={onChange(...prepChange(ANIMATION_DURATION), false)}
              />

              <Input
                label="Delay"
                type={INPUT_TYPE_NUMBER}
                min="0"
                disabled={isLocked}
                step=".25"
                value={getSyncValue(ANIMATION_DELAY)}
                units="sec"
                onSIBeforeChange={onChange(...prepChange(ANIMATION_DELAY))}
                onSIInput={onChange(...prepChange(ANIMATION_DELAY), false)}
              />
            </>
          )}
          {TYPE_DIRECTION_OPTIONS[type] && (
            <Select
              disabled={isLocked}
              reserveContext
              label={
                transitionType === STATE_TRANSITION_IN ||
                transitionType === TRANSITION_IN ||
                transitionType === DEFAULT_TRANSITION_IN
                  ? "From"
                  : "To"
              }
              onSISelect={onChange(...prepChange(ANIMATION_DIRECTION))}
              shift="left"
              value={getSyncValue(ANIMATION_DIRECTION)}
            >
              {TYPE_DIRECTION_OPTIONS[type].map(([value, label]) => (
                <SelectOption key={value} value={value}>
                  {label}
                </SelectOption>
              ))}
            </Select>
          )}
        </LeftOffset>
      </When>
    </SplitMergeWrapper>
  );
};

export default AnimationProperties;
